import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { BasicFacility, FacilityContact } from '../../../../../js/api';
import { HealthcareEntitiesService } from '../../../../../js/api';
import { useDebounce } from '../../../hooks/useDebounce';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { contactDisplayDetail, formatContactSortName, pluralize } from './contactDisplay';
import type { PickerItem } from './LinkPickerList';
import { contactQueryKeys } from './queryKeys';

const PICKER_LIMIT = 50;
/** Links loaded per remove picker; `truncated` tells the dialog when more exist. */
export const LINKS_LIMIT = 1000;
const SEARCH_DEBOUNCE_MS = 300;

export type LinkDialogMode = 'add' | 'remove';
export type LinkDirection = 'contactsToFacilities' | 'facilitiesToContacts';
/**
 * What the dialog's checklist holds: facilities / contacts of the org (add), the
 * selected contacts' linked facilities or the selected facilities' linked
 * contacts (remove).
 */
export type PickerKind = 'facilities' | 'contacts' | 'linkedFacilities' | 'linkedContacts';

export const isRemoveKind = (kind: PickerKind) =>
  kind === 'linkedFacilities' || kind === 'linkedContacts';

export const pickerKindFor = (mode: LinkDialogMode, direction: LinkDirection): PickerKind => {
  if (direction === 'facilitiesToContacts') {
    return mode === 'remove' ? 'linkedContacts' : 'contacts';
  }
  return mode === 'remove' ? 'linkedFacilities' : 'facilities';
};

const facilityItem = (facility: Pick<BasicFacility, 'id' | 'name' | 'city' | 'state'>) => ({
  id: facility.id ?? '',
  label: facility.name ?? '--',
  detail: getFacilityLocationDisplayValue(facility) ?? undefined,
});

/** Unique facilities across the selected contacts' links, optionally in one role. */
export const linkedFacilityItems = (
  links: FacilityContact[],
  role: string | null
): PickerItem[] => {
  const byId = new Map<string, PickerItem>();
  links
    .filter((link) => !role || link.role === role)
    .forEach((link) => {
      if (link.facility.id && !byId.has(link.facility.id)) {
        byId.set(link.facility.id, facilityItem(link.facility));
      }
    });
  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Unique contacts across the selected facilities' links, optionally in one role;
 * each detail line lists the facilities (of the selection) and roles held there.
 */
export const linkedContactItems = (
  links: FacilityContact[],
  role: string | null,
  roleLabel: (code: string) => string
): PickerItem[] => {
  const byId = new Map<string, { label: string; where: string[] }>();
  links
    .filter((link) => !role || link.role === role)
    .forEach((link) => {
      const contactId = link.contact.id;
      if (!contactId) return;
      const entry = byId.get(contactId) ?? {
        label: formatContactSortName(link.contact),
        where: [],
      };
      entry.where.push(`${link.facility.name ?? '--'} (${roleLabel(link.role)})`);
      byId.set(contactId, entry);
    });
  return Array.from(byId.entries())
    .map(([id, { label, where }]) => ({ id, label, detail: [...where].sort().join(' · ') }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/** `subjectName` names the single selected contact (or facility, facilities → contacts). */
export const linkDialogTitle = (
  kind: PickerKind,
  contactCount: number,
  facilityCount: number,
  subjectName?: string
): string => {
  if (kind === 'contacts' || kind === 'linkedContacts') {
    const where = subjectName ?? pluralize(facilityCount, 'facility', 'facilities');
    return kind === 'contacts' ? `Add contacts to ${where}` : `Remove contacts from ${where}`;
  }
  const who = subjectName ?? pluralize(contactCount, 'contact');
  return kind === 'linkedFacilities' ? `Remove ${who} from facilities` : `Add ${who} to facilities`;
};

/** itemId → subjectId → roles already held (facility items keyed by contact, or vice versa). */
export type ExistingRoles = Map<string, Map<string, Set<string>>>;

export const indexExistingRoles = (
  links: FacilityContact[],
  itemIdOf: (link: FacilityContact) => string | undefined,
  subjectIdOf: (link: FacilityContact) => string | undefined
): ExistingRoles => {
  const index: ExistingRoles = new Map();
  links.forEach((link) => {
    const itemId = itemIdOf(link);
    const subjectId = subjectIdOf(link);
    if (!itemId || !subjectId) return;
    const bySubject = index.get(itemId) ?? new Map<string, Set<string>>();
    const roles = bySubject.get(subjectId) ?? new Set<string>();
    roles.add(link.role);
    bySubject.set(subjectId, roles);
    index.set(itemId, bySubject);
  });
  return index;
};

/**
 * Add-mode picker items against what the selection already holds: once a role is
 * chosen, drop items where EVERY selected subject already has it (noise — the
 * link would be skipped); with a single subject, list their current roles there.
 */
export const applyExistingRoles = (
  items: PickerItem[],
  existing: ExistingRoles,
  subjectIds: string[],
  role: string | null,
  roleLabel: (code: string) => string
): { items: PickerItem[]; hiddenIds: Set<string> } => {
  const hiddenIds = new Set<string>();
  const visible = items.flatMap((item) => {
    const bySubject = existing.get(item.id);
    const alreadyAll =
      role !== null &&
      subjectIds.length > 0 &&
      subjectIds.every((subjectId) => bySubject?.get(subjectId)?.has(role));
    if (alreadyAll) {
      hiddenIds.add(item.id);
      return [];
    }
    const current = subjectIds.length === 1 ? bySubject?.get(subjectIds[0]) : undefined;
    if (!current || current.size === 0) return [item];
    const roles = Array.from(current).map(roleLabel).sort().join(', ');
    return [{ ...item, detail: [item.detail, `Current: ${roles}`].filter(Boolean).join(' · ') }];
  });
  return { items: visible, hiddenIds };
};

/**
 * Empty-picker message for add mode: a search that matched nothing, a role that
 * every remaining item already holds, or genuinely nothing in the org.
 */
export const addPickerEmptyText = (
  kind: PickerKind,
  search: string,
  hiddenCount: number,
  subjectCount: number
): string => {
  if (search.trim()) return 'Nothing matches your search criteria, clear or refine';
  const picking = kind === 'contacts' ? 'contacts' : 'facilities';
  if (hiddenCount > 0) {
    let owner: string;
    if (kind === 'contacts') owner = subjectCount === 1 ? 'this facility' : 'these facilities';
    else owner = subjectCount === 1 ? 'this contact' : 'these contacts';
    return `No remaining ${picking} for ${owner} in the chosen role`;
  }
  return `No ${picking} found.`;
};

/** A DRF 400 body as one readable message (view-level list, non_field_errors, field errors). */
export const apiErrorMessage = (error: unknown): string => {
  const body = (error as { body?: unknown })?.body;
  if (Array.isArray(body)) return body.join(' ');
  if (body && typeof body === 'object') {
    const text = Object.values(body as Record<string, unknown>)
      .flat()
      .filter((value) => typeof value === 'string')
      .join(' ');
    if (text) return text;
  }
  return 'Something went wrong. Please try again.';
};

type UseLinkDialogDataOptions = {
  open: boolean;
  kind: PickerKind;
  contactIds: string[];
  facilityIds: string[];
  healthcareEntityId?: string;
  search: string;
  role: string | null;
  roleLabel: (code: string) => string;
};

/**
 * Checklist items for the link dialog: facilities or contacts of the acting-as
 * org (add), or the facilities the selected contacts are linked to (remove).
 */
export function useLinkDialogData({
  open,
  kind,
  contactIds,
  facilityIds,
  healthcareEntityId,
  search,
  role,
  roleLabel,
}: UseLinkDialogDataOptions) {
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const orgPickerEnabled = open && Boolean(healthcareEntityId);

  const facilitiesQuery = useQuery({
    queryKey: [
      ...contactQueryKeys.all,
      'picker',
      'facilities',
      healthcareEntityId,
      debouncedSearch,
    ],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitiesList({
        healthcareEntity: healthcareEntityId,
        search: debouncedSearch || undefined,
        ordering: 'name',
        limit: PICKER_LIMIT,
      }),
    enabled: orgPickerEnabled && kind === 'facilities',
  });

  const contactsQuery = useQuery({
    queryKey: [...contactQueryKeys.all, 'picker', 'contacts', healthcareEntityId, debouncedSearch],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesContactsList({
        healthcareEntity: healthcareEntityId,
        search: debouncedSearch || undefined,
        ordering: 'last_name',
        limit: PICKER_LIMIT,
      }),
    enabled: orgPickerEnabled && kind === 'contacts',
  });

  const linksQuery = useQuery({
    queryKey: contactQueryKeys.links(contactIds),
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsList({
        contact: contactIds,
        limit: LINKS_LIMIT,
      }),
    enabled: open && kind !== 'contacts' && contactIds.length > 0,
  });

  // Facilities → contacts: what the selected facilities already hold (add mode
  // hides/annotates by it; remove mode lists exactly these contacts).
  const facilityLinksQuery = useQuery({
    queryKey: [...contactQueryKeys.all, 'links-by-facility', facilityIds],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsList({
        facilities: facilityIds,
        limit: LINKS_LIMIT,
      }),
    enabled: open && (kind === 'contacts' || kind === 'linkedContacts') && facilityIds.length > 0,
  });

  const baseItems = useMemo((): PickerItem[] => {
    if (kind === 'linkedFacilities') {
      return linkedFacilityItems(linksQuery.data?.results ?? [], role);
    }
    if (kind === 'linkedContacts') {
      return linkedContactItems(facilityLinksQuery.data?.results ?? [], role, roleLabel);
    }
    if (kind === 'contacts') {
      return (contactsQuery.data?.results ?? []).flatMap((contact) =>
        contact.id
          ? [
              {
                id: contact.id,
                label: formatContactSortName(contact),
                detail: contactDisplayDetail(contact),
              },
            ]
          : []
      );
    }
    return (facilitiesQuery.data?.results ?? []).map(facilityItem);
  }, [
    kind,
    linksQuery.data,
    facilityLinksQuery.data,
    contactsQuery.data,
    facilitiesQuery.data,
    role,
    roleLabel,
  ]);

  const { items, hiddenIds } = useMemo(() => {
    if (isRemoveKind(kind)) return { items: baseItems, hiddenIds: new Set<string>() };
    const pickingContacts = kind === 'contacts';
    const links = (pickingContacts ? facilityLinksQuery.data : linksQuery.data)?.results ?? [];
    const existing = pickingContacts
      ? indexExistingRoles(
          links,
          (link) => link.contact.id,
          (link) => link.facility.id
        )
      : indexExistingRoles(
          links,
          (link) => link.facility.id,
          (link) => link.contact.id
        );
    return applyExistingRoles(
      baseItems,
      existing,
      pickingContacts ? facilityIds : contactIds,
      role,
      roleLabel
    );
  }, [
    kind,
    baseItems,
    facilityLinksQuery.data,
    linksQuery.data,
    facilityIds,
    contactIds,
    role,
    roleLabel,
  ]);

  const activeQuery = {
    facilities: facilitiesQuery,
    contacts: contactsQuery,
    linkedFacilities: linksQuery,
    linkedContacts: facilityLinksQuery,
  }[kind];

  // Remove pickers are built from one page of links; past LINKS_LIMIT the list is partial.
  const truncated = isRemoveKind(kind) && Boolean(activeQuery.data?.next);

  return { items, hiddenIds, isLoading: activeQuery.isInitialLoading, truncated };
}
